import React, { useMemo, useState } from 'react';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Pagination, EffectCreative } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/effect-creative';

import { ReactComponent as PlaceholderIcon } from '../../../assets/icons/placeholder-icon.svg';

import styles from './CrystalsCards.module.scss';
import { CrystalListItem } from './types';

type RenderItem = { kind: 'item'; item: CrystalListItem } | { kind: 'stub' };
type Props = { items: CrystalListItem[] };

const STUB: RenderItem = { kind: 'stub' } as const;

function isStub(e: RenderItem): e is { kind: 'stub' } {
  return e.kind === 'stub';
}
function isItem(e: RenderItem): e is { kind: 'item'; item: CrystalListItem } {
  return e.kind === 'item';
}

function CrystalCard({ item, imageSrc, index }: { item: CrystalListItem; imageSrc: string; index: number }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className={styles.card} style={{ animationDelay: `${index * 0.08}s` }}>
      <img
        className={`${styles.cardBody} ${loaded ? styles.cardBodyLoaded : styles.cardBodyLoading}`}
        src={imageSrc}
        alt={item.name}
        loading="lazy"
        onLoad={() => setLoaded(true)}
      />
      <div className={styles.cardFooter}>{item.name}</div>
    </div>
  );
}

function paginate(entries: RenderItem[]): RenderItem[][] {
  const pages: RenderItem[][] = [];
  for (let i = 0; i < entries.length; i += 4) pages.push(entries.slice(i, i + 4) as RenderItem[]);
  const last = pages[pages.length - 1];
  if (last && last.length === 3) last.push(STUB);
  if (pages.length === 0) pages.push([]);
  return pages;
}

export function CrystalGrid({ items }: Props) {
  const pages: RenderItem[][] = useMemo(() => {
    const entries: RenderItem[] = items.map(it => ({ kind: 'item', item: it }));
    if (entries.length === 3) return [[...entries, STUB] as RenderItem[]];
    return paginate(entries);
  }, [items]);

  const slideClass = (entries: RenderItem[]) => {
    const realCount = entries.filter(isItem).length;
    if (realCount === 1) return styles.solo;
    if (realCount === 2) return styles.twoRows;
    return styles.grid2;
  };

  return (
    <div className={styles.wrap}>
      <Swiper
        className={styles.swiper}
        direction="vertical"
        slidesPerView={1}
        speed={800}
        cssMode={false}
        effect="creative"
        creativeEffect={{
          prev: { translate: [0, '-100%', 0], opacity: 0.85, scale: 0.96 },
          next: { translate: [0, '100%', 0], opacity: 0.85, scale: 0.96 },
          limitProgress: 2,
          perspective: false,
        }}
        mousewheel={{ forceToAxis: true, thresholdDelta: 6, sensitivity: 2 }}
        pagination={{ clickable: true }}
        modules={[Mousewheel, Pagination, EffectCreative]}
      >
        {pages.map((entries, p) => (
          <SwiperSlide className={`${styles.slide} ${slideClass(entries)}`} key={p}>
            {entries.map((entry, idx) => {
              if (isStub(entry)) {
                return (
                  <div
                    className={`${styles.card} ${styles.stub}`}
                    key={`stub-${p}-${idx}`}
                    style={{ animationDelay: `${idx * 0.08}s` }}
                  >
                    <div className={styles.stubInner}>
                      <PlaceholderIcon className={styles.placeholderIcon} />
                    </div>
                  </div>
                );
              }
              const { item } = entry;
              const realCount = entries.filter(isItem).length;
              const imageSrc = realCount <= 2 && item.detailedImageUrl ? item.detailedImageUrl : item.imageUrl;

              return (
                <CrystalCard key={item.id ?? `${item.name}-${p}-${idx}`} item={item} imageSrc={imageSrc} index={idx} />
              );
            })}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

export default CrystalGrid;
